import { useState, useRef, useEffect, useCallback, useMemo } from 'react';

const WorkGridSection = ({ onNavigate }) => {
  const sectionRef = useRef(null);
  const [visibleCards, setVisibleCards] = useState([]);
  const [glitchTrigger, setGlitchTrigger] = useState(0);

  // Glitch Effect Trigger: Randomly activate a subtle glitch
  useEffect(() => {
    const interval = setInterval(() => {
      if (Math.random() < 0.2) { // 20% chance to glitch every few seconds
        setGlitchTrigger(prev => prev + 1);
      }
    }, 3000 + Math.random() * 2000); // Between 3 and 5 seconds

    return () => clearInterval(interval);
  }, []);

  // --- CARD DATA AND REVEAL LOGIC ---
  const services = useMemo(() => [
    {
      id: 0,
      icon: '⚡',
      title: 'Software Development',
      description: 'Full-stack applications and scalable digital products.',
      tech: ['React', 'Node.js', 'Postgres', 'Nuxt.js'],
      gradient: 'from-blue-500 to-cyan-500',
      animation: 'quantum-scan'
    },
    {
      id: 1,
      icon: '🔧',
      title: 'Tech Consultation',
      description: 'Digital transformation and enterprise solutions.',
      tech: ['Architecture', 'Payment Gateways', 'Cloud', 'Security', 'GIS'],
      gradient: 'from-green-500 to-emerald-500',
      animation: 'neural-activation'
    },
    {
      id: 2,
      icon: '🧠', 
      title: 'Data Science',
      description: 'Machine learning and intelligent automation.',
      tech: ['Python', 'ML', 'Tensorflow'],
      gradient: 'from-purple-500 to-indigo-500',
      animation: 'hologram-deploy'
    },
    {
      id: 3,
      icon: '💾',
      title: 'Hardware & IoT',
      description: 'Enterprise hardware and IoT solutions.',
      tech: ['IoT', 'Hardware', 'Networking'],
      gradient: 'from-orange-500 to-amber-500',
      animation: 'magnetic-field'
    }
  ], []);

  // Intersection Observer for staggered card reveal
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const cardId = entry.target.dataset.cardId;
            setTimeout(() => {
              setVisibleCards(prev => [...new Set([...prev, parseInt(cardId)])]);
            }, parseInt(cardId) * 200);
          }
        });
      },
      { threshold: 0.3 }
    );

    const cards = document.querySelectorAll('[data-card-id]');
    cards.forEach(card => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  // Memoized Animation Components (kept original logic)
  const QuantumScanReveal = useCallback(({ progress }) => (
    <>
      <div 
        className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-blue-500/5"
        style={{
          clipPath: `circle(${progress * 150}% at 0% 50%)`,
          transition: 'clip-path 1s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      />
      <div 
        className="absolute top-0 bottom-0 w-1 bg-gradient-to-b from-cyan-400 via-blue-400 to-transparent shadow-2xl shadow-cyan-400/60"
        style={{
          left: `${progress * 100}%`,
          transition: 'left 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
          filter: 'blur(0.5px)'
        }}
      />
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-cyan-400 rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              opacity: progress > 0.3 ? Math.random() * 0.5 : 0,
              animationDelay: `${i * 0.2}s`,
              transform: `translateX(${progress * 30}px)`
            }}
          />
        ))}
      </div>
      <div className="absolute inset-0 overflow-hidden opacity-30">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="absolute text-cyan-400 text-xs font-mono animate-pulse"
            style={{
              left: `${10 + i * 12}%`,
              top: `${-20 + progress * 120}%`,
              transition: 'top 0.8s ease-out',
              transitionDelay: `${i * 0.1}s`
            }}
          >
            {Math.random() > 0.5 ? '1' : '0'}
          </div>
        ))}
      </div>
    </>
  ), []);

  const NeuralActivationReveal = useCallback(({ progress }) => (
    <>
      <div className="absolute inset-0">
        {[...Array(4)].map((_, i) => (
          <div
            key={`h-${i}`}
            className="absolute left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-emerald-400 to-transparent"
            style={{
              top: `${(i / 3) * 100}%`,
              opacity: progress > (i / 4) ? 0.6 : 0,
              transition: `opacity 0.6s ease-out ${i * 0.15}s`
            }}
          />
        ))}
        {[...Array(6)].map((_, i) => (
          <div
            key={`v-${i}`}
            className="absolute top-0 bottom-0 w-0.5 bg-gradient-to-b from-transparent via-emerald-400 to-transparent"
            style={{
              left: `${(i / 5) * 100}%`,
              opacity: progress > (i / 6) ? 0.6 : 0,
              transition: `opacity 0.6s ease-out ${i * 0.15 + 0.2}s`
            }}
          />
        ))}
      </div>
      <div className="absolute inset-0">
        {[...Array(8)].map((_, i) => {
          const angle = (i / 8) * Math.PI * 2;
          const distance = 20 + progress * 40;
          return (
            <div
              key={i}
              className="absolute w-0.5 h-0.5 bg-emerald-400 rounded-full shadow-lg shadow-emerald-400"
              style={{
                left: `50%`,
                top: `50%`,
                transform: `translate(-50%, -50%) translate(${Math.cos(angle) * distance}px, ${Math.sin(angle) * distance}px)`,
                opacity: progress > 0.2 ? 0.8 : 0,
                transition: `all 0.8s ease-out ${i * 0.08}s`
              }}
            />
          );
        })}
      </div>
      <div 
        className="absolute inset-0 border-2 border-emerald-400 rounded-lg"
        style={{
          opacity: progress * 0.2,
          boxShadow: `0 0 ${progress * 20}px ${progress * 5}px rgba(52, 211, 153, 0.2)`,
          transition: 'all 1s ease-out'
        }}
      />
    </>
  ), []);

  const HologramDeployReveal = useCallback(({ progress }) => (
    <>
      <div className="absolute inset-0">
        <div 
          className="absolute inset-0 border-2 border-purple-400/50 rounded-lg"
          style={{
            transform: `perspective(1000px) rotateX(${5 + progress * 10}deg) rotateY(${progress * 5}deg)`,
            opacity: progress * 0.6,
            transition: 'all 0.8s ease-out'
          }}
        />
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-purple-400 rounded-full shadow-lg shadow-purple-400"
            style={{
              left: `${10 + (i % 4) * 25}%`,
              top: `${10 + Math.floor(i / 4) * 25}%`,
              opacity: progress > 0.4 ? 0.6 : 0,
              transform: `translateY(${Math.sin(progress * 10 + i) * 5}px)`,
              transition: `opacity 0.5s ease-out ${i * 0.03}s, transform 0.3s ease-out`
            }}
          />
        ))}
      </div>
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(2)].map((_, i) => (
          <div
            key={i}
            className="absolute left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-indigo-400 to-transparent shadow-lg shadow-indigo-400"
            style={{
              top: `${20 + i * 40}%`,
              opacity: progress > 0.2 ? 0.5 : 0,
              transform: `translateX(${progress * 100 - 100}%)`,
              transition: `transform 0.8s ease-out ${i * 0.3}s, opacity 0.3s ease-out`
            }}
          />
        ))}
      </div>
      <div 
        className="absolute inset-0 bg-gradient-to-br from-purple-400/10 via-transparent to-indigo-400/10"
        style={{
          opacity: (progress * 0.3) + (Math.sin(progress * 20) * 0.05),
          transition: 'opacity 0.1s ease-out'
        }}
      />
    </>
  ), []);

  const MagneticFieldReveal = useCallback(({ progress }) => (
    <>
      <div className="absolute inset-0 overflow-hidden">
        {/* Magnetic field lines */}
        {[...Array(5)].map((_, i) => (
          <div
            key={`field-${i}`}
            className="absolute inset-0 border-2 border-amber-400/30 rounded-lg"
            style={{
              transform: `
                scale(${1 + i * 0.1 * progress})
                rotate(${progress * (i % 2 === 0 ? 3 : -3)}deg)
              `,
              opacity: progress * (0.3 - i * 0.05),
              transition: `all 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55) ${i * 0.1}s`
            }}
          />
        ))}
      </div>
      {/* Ferrofluid particles */}
      {[...Array(12)].map((_, i) => {
        const angle = (i / 12) * Math.PI * 2;
        const distance = 15 + progress * 30;
        const spikeLength = 5 + Math.sin(progress * 20 + i) * 3;
        
        return (
          <div key={i} className="absolute inset-0">
            <div
              className="absolute w-2 h-2 bg-amber-500 rounded-full shadow-lg shadow-amber-400/50"
              style={{
                left: '50%',
                top: '50%',
                transform: `
                  translate(-50%, -50%)
                  translate(${Math.cos(angle) * distance}px, ${Math.sin(angle) * distance}px)
                `,
                opacity: progress > 0.3 ? 0.8 : 0,
                transition: `all 0.6s ease-out ${i * 0.05}s`
              }}
            />
            {/* Magnetic spikes */}
            <div
              className="absolute w-0.5 bg-gradient-to-b from-amber-400 to-transparent"
              style={{
                left: '50%',
                top: '50%',
                height: `${spikeLength}px`,
                transform: `
                  translate(-50%, -50%)
                  translate(${Math.cos(angle) * distance}px, ${Math.sin(angle) * distance}px)
                  rotate(${angle}rad)
                `,
                opacity: progress > 0.5 ? 0.6 : 0,
                transition: 'all 0.3s ease-out'
              }}
            />
          </div>
        );
      })}
    </>
  ), []);

  const getAnimationComponent = (animationType, progress) => {
    const components = {
      'quantum-scan': QuantumScanReveal,
      'neural-activation': NeuralActivationReveal,
      'hologram-deploy': HologramDeployReveal,
      'magnetic-field': MagneticFieldReveal
    };
    
    const Component = components[animationType];
    return Component ? <Component progress={progress} /> : null;
  };

  // --- JSX RENDER ---
  return (
    <>
      {/* Top Border */}
      <div className="relative h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-cyan-400 to-transparent animate-pulse shadow-lg shadow-cyan-500/25"></div>
      </div>

      <section ref={sectionRef} className="relative min-h-screen bg-black overflow-hidden">
        {/* NEW: Glitch Grid Background */}
        <div className="absolute inset-0 z-0 bg-black">
          {/* Main Grid Lines */}
          <div 
            className="absolute inset-0 opacity-10"
            style={{ 
                backgroundImage: `linear-gradient(to right, #1a1a1a 1px, transparent 1px), 
                                linear-gradient(to bottom, #1a1a1a 1px, transparent 1px)`,
                backgroundSize: '40px 40px',
                animation: 'grid-pulse 8s infinite alternate',
            }}
          />

          {/* Glitching Text Overlay */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            {[...Array(10)].map((_, i) => (
              <span 
                key={i} 
                className={`absolute text-6xl md:text-8xl lg:text-9xl font-mono text-cyan-900/10 select-none opacity-0 ${glitchTrigger ? 'glitch-text' : ''}`}
                style={{
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                  transform: `translate(-50%, -50%) scale(${0.8 + Math.random() * 0.4}) rotate(${Math.random() * 10 - 5}deg)`,
                  animation: `fade-in-out ${3 + Math.random() * 2}s infinite ease-out ${i * 0.5}s, 
                              move-random ${5 + Math.random() * 5}s infinite ease-in-out ${i * 0.7}s`,
                  animationDelay: `${i * 0.5}s`,
                }}
              >
                {Math.random() > 0.5 ? 'NIXQUE' : 'DATA'}
              </span>
            ))}
          </div>

          {/* Glitching Grid Lines (More subtle) */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(5)].map((_, i) => (
              <div 
                key={i}
                className={`absolute bg-cyan-500/5 top-0 w-full h-0.5 ${glitchTrigger ? 'glitch-line-h' : ''}`}
                style={{
                  top: `${20 + i * 15 + Math.sin(glitchTrigger * 0.1 + i) * 5}%`,
                  animationDelay: `${i * 0.3}s`,
                  opacity: 0.1
                }}
              />
            ))}
             {[...Array(5)].map((_, i) => (
              <div 
                key={i}
                className={`absolute bg-cyan-500/5 left-0 h-full w-0.5 ${glitchTrigger ? 'glitch-line-v' : ''}`}
                style={{
                  left: `${20 + i * 15 + Math.cos(glitchTrigger * 0.1 + i) * 5}%`,
                  animationDelay: `${i * 0.3 + 0.15}s`,
                  opacity: 0.1
                }}
              />
            ))}
          </div>
        </div>
        
        <div className="relative z-10 py-20">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-center text-white mb-16"
              style={{
                fontFamily: "'PP Eiko', sans-serif",
                fontWeight: 240,
                fontSize: '3rem',
                lineHeight: '110%',
                letterSpacing: '-0.02em',
              }}
            >
              Core Services
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {services.map((service) => {
                const isVisible = visibleCards.includes(service.id);
                const progress = isVisible ? 1 : 0;

                return (
                  <div
                    key={service.id}
                    data-card-id={service.id}
                    className="relative group overflow-hidden"
                  >
                    <div className="relative bg-gray-900/80 backdrop-blur-2xl border border-cyan-500/30 p-6 transition-all duration-500 group-hover:border-cyan-400/60 group-hover:transform group-hover:scale-[1.03] group-hover:shadow-2xl group-hover:shadow-cyan-500/20 h-full">
                      
                      {/* Dynamic Reveal Layer */}
                      {getAnimationComponent(service.animation, progress)}
                      
                      <div className="flex items-start gap-4 relative z-0">
                        <div 
                          className="text-4xl transform transition-all duration-500 group-hover:scale-110 group-hover:rotate-12 group-hover:text-cyan-400 flex-shrink-0"
                          style={{
                            opacity: progress > 0.3 ? 1 : 0,
                            transform: `translateY(${progress > 0.3 ? 0 : 20}px) rotate(${progress > 0.3 ? 0 : 30}deg)`,
                            transition: 'all 0.6s ease-out 0.3s'
                          }}
                        >
                          {service.icon}
                        </div>
                        
                        <div 
                          className="flex-1 min-w-0"
                          style={{
                            opacity: progress > 0.5 ? 1 : 0,
                            transform: `translateY(${progress > 0.5 ? 0 : 10}px)`,
                            transition: 'all 0.6s ease-out 0.5s'
                          }}
                        >
                          <h3 className="text-white text-xl font-semibold mb-2 font-mono leading-tight bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                            {service.title}
                          </h3>
                          <p className="text-white/70 text-sm leading-relaxed mb-3">
                            {service.description}
                          </p>
                          
                          <div className="flex flex-wrap gap-1.5">
                            {service.tech.map((tech, techIndex) => (
                              <span 
                                key={techIndex}
                                className="px-2 py-1 bg-cyan-500/15 border border-cyan-500/30 rounded-lg text-cyan-400 text-xs font-mono transition-all duration-300 group-hover:bg-cyan-500/25 group-hover:border-cyan-400/50 group-hover:scale-105"
                                style={{
                                  opacity: progress > 0.7 ? 1 : 0,
                                  transform: `translateX(${progress > 0.7 ? 0 : -10}px)`,
                                  transition: `all 0.4s ease-out ${techIndex * 0.1 + 0.7}s`
                                }}
                              >
                                {tech}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="max-w-2xl mx-auto mt-16 text-center">
              <p className="text-white/60 text-sm font-mono mb-8 tracking-wider">
                INNOVATION • PRECISION • EXCELLENCE
              </p>
            </div>
          </div>
        </div>

        <FooterSection onNavigate={onNavigate} />

        {/* Tailwind CSS Keyframes for Glitch Grid Background */}
        <style jsx global>{`
          @keyframes grid-pulse {
            0% { opacity: 0.1; }
            50% { opacity: 0.05; }
            100% { opacity: 0.1; }
          }

          .glitch-text {
            animation: text-glitch 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
            animation-iteration-count: 1; /* Only run once per trigger */
          }

          @keyframes text-glitch {
            0% {
              transform: translate(-50%, -50%) scale(1.1) rotate(2deg);
              filter: blur(2px);
              opacity: 0.1;
            }
            20% {
              transform: translate(-48%, -52%) scale(0.9) rotate(-1deg);
              filter: blur(0px);
              opacity: 0.2;
            }
            40% {
              transform: translate(-51%, -49%) scale(1.05) rotate(1deg);
              filter: blur(1px);
              opacity: 0.15;
            }
            60% {
              transform: translate(-49%, -51%) scale(0.95) rotate(-0.5deg);
              filter: blur(0.5px);
              opacity: 0.25;
            }
            80% {
              transform: translate(-50.5%, -50.5%) scale(1.02) rotate(0.5deg);
              filter: blur(0px);
              opacity: 0.18;
            }
            100% {
              transform: translate(-50%, -50%) scale(1) rotate(0deg);
              filter: blur(0px);
              opacity: 0.1;
            }
          }

          .glitch-line-h {
            animation: line-glitch-h 0.2s linear both;
            animation-iteration-count: 1;
          }

          @keyframes line-glitch-h {
            0% { transform: scaleX(0); opacity: 0.5; }
            50% { transform: scaleX(1.1); opacity: 0.8; }
            100% { transform: scaleX(0); opacity: 0; }
          }

          .glitch-line-v {
            animation: line-glitch-v 0.2s linear both;
            animation-iteration-count: 1;
          }

          @keyframes line-glitch-v {
            0% { transform: scaleY(0); opacity: 0.5; }
            50% { transform: scaleY(1.1); opacity: 0.8; }
            100% { transform: scaleY(0); opacity: 0; }
          }
          
          @keyframes fade-in-out {
            0% { opacity: 0; }
            20% { opacity: 0.1; }
            80% { opacity: 0.1; }
            100% { opacity: 0; }
          }

          @keyframes move-random {
            0% { transform: translate(-50%, -50%); }
            25% { transform: translate(-48%, -52%); }
            50% { transform: translate(-52%, -49%); }
            75% { transform: translate(-49%, -51%); }
            100% { transform: translate(-50%, -50%); }
          }
        `}</style>
      </section>
    </>
  );
};

// Footer Section Component (retained from original prompt)
const FooterSection = ({ onNavigate }) => {
  const [currentYear] = useState(new Date().getFullYear());

  const socialLinks = [
    {
      name: 'X',
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      ),
      url: 'https://x.com/nixque'
    },
    {
      name: 'Instagram',
      icon: (
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 5.378c-3.57 0-6.49 2.92-6.49 6.49s2.92 6.49 6.49 6.49 6.49-2.92 6.49-6.49-2.92-6.49-6.49-6.49zm0 10.665c-2.316 0-4.187-1.87-4.187-4.187 0-2.317 1.87-4.187 4.187-4.187 2.317 0 4.187 1.87 4.187 4.187 0 2.316-1.87 4.187-4.187 4.187zm5.244-10.421c0 .61-.494 1.104-1.104 1.104-.61 0-1.104-.494-1.104-1.104s.494-1.104 1.104-1.104c.61 0 1.104.494 1.104 1.104z"/>
        </svg>
      ),
      url: 'https://instagram.com/nixque'
    }
  ];

  return (
    <footer className="relative z-10 py-8 bg-black/80 border-t border-cyan-500/20 backdrop-blur-sm">
      <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center text-xs text-white/50 font-mono">
        <p>&copy; {currentYear} NIXQUE. All Rights Reserved.</p>
        
        <div className="flex space-x-4 mt-4 md:mt-0">
          <button 
            onClick={() => onNavigate('contact')}
            className="text-white/70 hover:text-cyan-400 transition-colors tracking-widest uppercase hover:underline"
          >
            Contact
          </button>
          <button 
            onClick={() => onNavigate('privacy')}
            className="text-white/70 hover:text-cyan-400 transition-colors tracking-widest uppercase hover:underline"
          >
            Privacy
          </button>
        </div>

        <div className="flex space-x-3 mt-4 md:mt-0">
          {socialLinks.map(link => (
            <a
              key={link.name}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/70 hover:text-cyan-400 transition-colors"
              aria-label={link.name}
            >
              {link.icon}
            </a>
          ))}
        </div>
      </div>
    </footer>
  );
};

export default WorkGridSection;